import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Select, Spin } from "antd";
import { useQuery } from "react-query";
import useAxiosInstance from "../../lib/useAxiosInstance";
import "./ElectionModeDashboard.css";
import RightPanel, { MlaAffidavitPanel, NotesPanel, NewsPanelClient } from "./RightPanel";
import { useNavigate } from "react-router-dom";
import { AlignLeft, AlignRight, StickyNote, FileSpreadsheet, Newspaper } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Tooltip } from "antd";
import formatIndianNumber from "../../utils/formatIndianNumber";
import { useTranslation } from "react-i18next";

// ─── Filter constants ─────────────────────────────────────────────────────────
const formatPercent = (value) => {
  const number = Number(value) || 0;
  const percent = Math.abs(number) <= 1 ? number * 100 : number;
  return `${percent.toFixed(1)}%`;
};


const CASTE_FILTERS = [
  { label: "All Castes", keys: ["SC", "ST", "OBC", "Muslim", "Brahmins", "Non-Brahmins", "Other"] },
  { label: "OBC", keys: ["OBC"] },
  { label: "SC/ST", keys: ["SC", "ST"] },
  { label: "General", keys: ["Brahmins", "Non-Brahmins", "Other"] },
  { label: "Muslim", keys: ["Muslim"] },
];

const RELIGION_FILTERS = ["All Religions", "Hindu", "Muslim", "Sikh"];
const GENDER_FILTERS = ["All", "Male", "Female"];
const AGE_BUCKETS = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];

// Maps the internal (English) filter labels to their translation keys so the
// display text can be localized while the logic keeps using the stable label.
const CASTE_LABEL_KEYS = {
  "All Castes": "allCastes",
  OBC: "obc",
  "SC/ST": "scst",
  General: "general",
  Muslim: "muslim",
};
const GENDER_LABEL_KEYS = { All: "all", Male: "male", Female: "female" };

// ─── Small reusable components ────────────────────────────────────────────────

const MiniStat = ({ label, value, raw }) => (
  <div className="card-wrapper">
    <div className="election-mini-value">
      {raw ? value || 0 : formatIndianNumber(value)}
    </div>
    <div className="election-mini-label">{label}</div>
  </div>
);

const BottomStat = ({ label, value, sub, green, red }) => (
  <div className="election-stat">
    <div className="election-kicker">{label}</div>
    <div className="election-stat-value">
      {typeof value === "string" ? value : formatIndianNumber(value)}
    </div>
    <div
      className="election-stat-sub"
      style={{ color: green ? "#37d67a" : red ? "#ff4d4f" : undefined }}
    >
      {sub}
    </div>
  </div>
);

// Party → brand colour map. Add/adjust parties here to recolour their labels.
const PARTY_COLORS = {
  BJP: "#f97316",
  SP: "#ef4444",
  INC: "#16a34a",
  CONGRESS: "#16a34a",
  BSP: "#2563eb",
  ADAL: "#22c55e",
  RLD: "#14b8a6",
  AAP: "#0ea5e9",
  AIMIM: "#15803d",
  NISHAD: "#a855f7",
  SBSP: "#eab308",
  DEFAULT: "#94a3b8",
};

const getPartyColor = (party) =>
  PARTY_COLORS[String(party || "").trim().toUpperCase()] ||
  party?.color ||
  PARTY_COLORS.DEFAULT;

// Party tile for the bottom stats bar — party name on top (coloured),
// the metric (seat count / vote-share) below.
const PartyStat = ({ party, value, rankLabel, sub }) => (
  <div className="election-stat">
    <div className="election-kicker">
      {rankLabel}
    </div>
    <div className="election-stat-value" style={{ color: getPartyColor(party) }}>
       {party || "-"}
    </div>
    <div className="election-stat-sub">
      {typeof value === "string" ? value : formatIndianNumber(value)}
      {sub ? ` ${sub}` : ""}
    </div>
  </div>
);

// ─── Left Panel ───────────────────────────────────────────────────────────────

const LeftPanel = ({
  data,
  filters,
  isLoading,
  onDistrictChange,
  onConstituencyChange,
  onPartyChange,
  parties,
  overview,
  community,
  religion,
  age,
  activeCaste,
  setActiveCaste,
  activeReligion,
  setActiveReligion,
  activeGender,
  setActiveGender,
  activeAge,
  setActiveAge,
  casteNumber,
  religionNumber,
  genderNumber,
  genderLabel,
  t
}) => (
  <div className="election-dashboard-left card-wrapper">
    {isLoading ? (
      <div className="election-section">
        <Spin />
      </div>
    ) : (
      <>
        {/* Electoral Distribution */}
        <div className="election-section">
          <div className="election-section-title">{t("ElectionMode.electoralDistribution")}</div>
          <div className="election-mini-grid">
            <MiniStat label={t("ElectionMode.totalVoters")} value={formatIndianNumber(overview.totalVoters)} />
            <MiniStat label={t("ElectionMode.activeVoters")} value={overview.activeVoters} />
            <MiniStat label={t("ElectionMode.inactiveVoters")} value={overview.inactiveVoters} />
            <MiniStat label={t("ElectionMode.averageAge")} value={overview.averageAge} raw />
            {/* <MiniStat label={t("ElectionMode.sirDeduction")} value={data?.panels?.electionResult?.sirDeduction} /> */}
            {/* <MiniStat label={t("ElectionMode.sirNumberOfBooths")} value={data?.panels?.electionResult?.sirNumberOfBooths} /> */}
          </div>
        </div>

        {/* Demographics */}
        <div className="election-section">
          <div className="election-section-title">{t("ElectionMode.demographics")}</div>
          <div className="election-chip-row">
            {CASTE_FILTERS.map((item) => (
              <button
                key={item.label}
                className={`election-chip ${activeCaste === item.label ? "active" : ""}`}
                onClick={() => setActiveCaste(item.label)}
              >
                {t(`ElectionMode.${CASTE_LABEL_KEYS[item.label] || "allCastes"}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-col justify-center items-center m-2">
            <div className="election-stat-value !text-[22px]">
              {formatIndianNumber(casteNumber)}
            </div>
            <div className="election-mini-label">
              {t("ElectionMode.totalVotersIn", {
                group: t(`ElectionMode.${CASTE_LABEL_KEYS[activeCaste] || "allCastes"}`),
              })}
            </div>
          </div>
        </div>

        {/* Gender & Age */}
        <div className="election-section">
          <div className="election-section-title">{t("ElectionMode.genderAge")}</div>
          <div className="election-chip-row">
            {GENDER_FILTERS.map((item) => (
              <button
                key={item}
                className={`election-chip ${activeGender === item ? "active" : ""}`}
                onClick={() => setActiveGender(item)}
              >
                {t(`ElectionMode.${GENDER_LABEL_KEYS[item] || "all"}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-col justify-center items-center m-2">
            <div className="election-stat-value !text-[22px]">{genderLabel}</div>
            <div className="election-mini-label">
              {t("ElectionMode.totalGenderVoters", {
                gender: t(`ElectionMode.${GENDER_LABEL_KEYS[activeGender] || "all"}`),
              })}
            </div>
          </div>

          <div className="election-chip-row mt-4">
            {AGE_BUCKETS.map((item) => (
              <button
                key={item}
                className={`election-chip ${activeAge === item ? "active" : ""}`}
                onClick={() => setActiveAge(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="flex flex-col justify-center items-center m-2">
            <div className="election-stat-value !text-[20px]">
              {formatIndianNumber(age[activeAge])}
            </div>
            <div className="election-mini-label">{t("ElectionMode.ageVoters", { age: activeAge })}</div>
          </div>
        </div>
      </>
    )}
  </div>
);

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const ElectionModeDashboard = ({
  electionModeId,
  selectedConstituencyFromMap,
  mapAction,
  onLocationSelect,
  onDashboardMapChange,
  leftPanelOpen,
  setLeftPanelOpen,
  filters,
  setFilters,
  // Called once on mount, passes back a `clearAllLocationFilters` function
  // so ElectionModeMapView can trigger a filter reset when an InfoWindow closes.
  onClearReady,
  // Called whenever filter option lists are ready (regions/districts/constituencies/parties)
  onFilterOptionsReady,
  electionModeType,
  urbanRural,
}) => {
  const { t } = useTranslation();
  // Assembly mode swaps Constituency/District wording for Ward (Urban) or
  // Village/Block (Rural) — the underlying filter params are unchanged.
  const isRuralAssembly = electionModeType === "assembly" && urbanRural === "Rural";
  const isUrbanAssembly =
    electionModeType === "assembly" && (urbanRural === "Urban" || !urbanRural);
  const districtLabel = isRuralAssembly
    ? t("ElectionMode.block")
    : t("ElectionMode.district");
  const constituencyLabel = isRuralAssembly
    ? t("ElectionMode.village")
    : isUrbanAssembly
    ? t("ElectionMode.ward")
    : t("ElectionMode.constituency");
  const navigate = useNavigate();
  const axiosInstance = useAxiosInstance();
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showMlaPanel, setShowMlaPanel] = useState(false);
  const [showNewsPanel, setShowNewsPanel] = useState(false);

  // Navigate to the dedicated Media Intelligence page, carrying the current
  // location filters as query params so the page lands pre-filtered.
  const openMediaIntelligencePage = () => {
    const params = new URLSearchParams();
    if (filters.region) params.set("region", filters.region);
    if (filters.district) params.set("district", filters.district);
    if (filters.constituency) params.set("constituency", filters.constituency);
    if (filters.party) params.set("party", filters.party);
    const qs = params.toString();
    navigate(`/media-intelligence${qs ? `?${qs}` : ""}`);
  };
  const [activeRightTab, setActiveRightTab] = useState("Electoral");
  const [quickNoteRequest, setQuickNoteRequest] = useState(null);
  const [quickReportRequest, setQuickReportRequest] = useState(null);
  const [activeCaste, setActiveCaste] = useState(CASTE_FILTERS[0].label);
  const [activeReligion, setActiveReligion] = useState(RELIGION_FILTERS[0]);
  const [activeGender, setActiveGender] = useState(GENDER_FILTERS[0]);
  const [activeAge, setActiveAge] = useState(AGE_BUCKETS[0]);
  const [expandedPanels, setExpandedPanels] = useState({
    electionResult: true,
    favourability: false,
    community: false,
    religion: false,
    locations: false,
  });

  const togglePanel = (key) =>
    setExpandedPanels((prev) => ({ ...prev, [key]: !prev[key] }));

  // ─── Clear all location filters ──────────────────────────────────────────────
  // This function is passed up to ElectionModeMapView via onClearReady so that
  // closing an InfoWindow on the map can reset the TopFilter dropdowns.

  const clearAllLocationFilters = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      region: "",
      district: "",
      constituency: "",
    }));
    onLocationSelect?.(null);
  }, [onLocationSelect]);

  // Register the clear function with the parent map view once it's stable.
  useEffect(() => { 
    onClearReady?.(clearAllLocationFilters);
  }, [onClearReady, clearAllLocationFilters]);

  // ─── Sync constituency from map click ────────────────────────────────────────

  useEffect(() => {
    if (!selectedConstituencyFromMap) return;
    setFilters((prev) => ({
      ...prev,
      region: "",
      district: "",
      constituency: selectedConstituencyFromMap,
    }));
  }, [selectedConstituencyFromMap]);

  // ─── Handle map actions (note / report triggers) ──────────────────────────────

  useEffect(() => {
    if (!mapAction?.id) return;
    if (mapAction.type === "note") {
      setShowNotesPanel(true);
      setShowRightPanel(false);
      setShowMlaPanel(false);
      setQuickNoteRequest({
        id: mapAction.id,
        preset: {
          party: filters.party,
          ...(mapAction.scope === "district"
            ? { scope: "district", district: mapAction.district }
            : { scope: "constituency", constituency: mapAction.constituency }),
        },
      });
    }
    if (mapAction.type === "report") {
      setShowNotesPanel(false);
      setShowRightPanel(true);
      setActiveRightTab("Reports");
      setQuickReportRequest({ id: mapAction.id });
    }
  }, [mapAction?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Data fetch ───────────────────────────────────────────────────────────────

  const { data, isLoading } = useQuery(
    ["electionModeDashboard", electionModeId, filters],
    async () => {
      const res = await axiosInstance.get(
        `/api/electionMode/dashboard/${electionModeId}`,
        {
          params: {
            district: filters.district || undefined,
            region: filters.region || undefined,
            constituency: filters.constituency || undefined,
            party: filters.party || "BJP",
          },
        }
      );
      return res.data?.data;
    },
    { enabled: !!electionModeId, refetchOnWindowFocus: false, retry: 1 }
  );

  // ─── Propagate map state upward ───────────────────────────────────────────────

  useEffect(() => {
    if (!data) return;
    onDashboardMapChange?.({
      region: filters.region,
      district: filters.district,
      constituency: filters.constituency,
      selectedConstituencies: data.map?.selectedConstituencies || [],
    });
  }, [data, filters.region, filters.district, filters.constituency, onDashboardMapChange]);

  // ─── Parties list & auto-select ───────────────────────────────────────────────

  const parties = useMemo(() => data?.filters?.parties || [], [data]);
  useEffect(() => {
    if (!parties.length || parties.includes(filters.party)) return;
    setFilters((prev) => ({
      ...prev,
      party: parties.includes("BJP") ? "BJP" : parties[0],
    }));
  }, [parties, filters.party]);

  // ─── Derived data ─────────────────────────────────────────────────────────────

  const overview = useMemo(() => data?.electoralDistribution || {}, [data]);
  const community = useMemo(() => overview.communityDistribution || {}, [overview]);
  const religion = useMemo(() => overview.religionDistribution || {}, [overview]);
  const age = useMemo(() => overview.ageDistribution || {}, [overview]);
  const bottomStats = data?.bottomStats || {};
  const panels = data?.panels || {};

  const communityChart = Object.entries(panels.communityDistribution || {}).map(
    ([name, value]) => ({ name, value })
  );
  const religionChart = Object.entries(panels.religionDistribution || {}).map(
    ([name, value]) => ({ name, value })
  );

  // ─── Demographic calculations ─────────────────────────────────────────────────

  const casteNumber = useMemo(() => {
    const selected = CASTE_FILTERS.find((item) => item.label === activeCaste);
    return (selected?.keys || []).reduce(
      (total, key) => total + (Number(community[key]) || 0),
      0
    );
  }, [activeCaste, community]);

  const religionNumber =
    activeReligion === "All Religions"
      ? Object.values(religion).reduce((t, v) => t + (Number(v) || 0), 0)
      : Number(religion[activeReligion]) || 0;

  const genderTotal = (overview.maleVoters || 0) + (overview.femaleVoters || 0);
  const genderNumber =
    activeGender === "Male"
      ? overview.maleVoters || 0
      : activeGender === "Female"
      ? overview.femaleVoters || 0
      : genderTotal;

  const genderLabel =
    activeGender === "All"
      ? formatIndianNumber(genderNumber)
      : `${formatIndianNumber(genderNumber)} (${Math.round(
          genderTotal ? (genderNumber / genderTotal) * 100 : 0
        )}%)`;

  // ─── Filter change handlers (used by LeftPanel) ──────────────────────────────

  const handleDistrictChange = useCallback((value) => {
    setFilters((prev) => ({
      ...prev,
      region: "",
      district: value || "",
      constituency: "",
    }));
  }, [setFilters]);

  const handleConstituencyChange = useCallback((value) => {
    setFilters((prev) => ({ ...prev, constituency: value || "" }));
  }, [setFilters]);

  // ─── Sync map location when district/region filter changes ──────────────────
  const prevDistrictRef = useRef(filters?.district);
  const prevRegionRef = useRef(filters?.region);
  useEffect(() => {
    if (
      prevDistrictRef.current !== filters?.district ||
      prevRegionRef.current !== filters?.region
    ) {
      onLocationSelect?.(null);
      prevDistrictRef.current = filters?.district;
      prevRegionRef.current = filters?.region;
    }
  }, [filters?.district, filters?.region, onLocationSelect]);

  useEffect(() => {
    onLocationSelect?.(filters?.constituency || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.constituency]);

  // ─── Notify parent when filter option lists are ready ────────────────────────
  useEffect(() => {
    onFilterOptionsReady?.({ data, parties });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, parties]);

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="election-dashboard">
      {/* Right panel toggle buttons */}
      <div className="absolute top-[46%] right-4 flex flex-col gap-3 z-[9999]">
        <Tooltip
          placement="left"
          title={`${t(showRightPanel ? "ElectionMode.close" : "ElectionMode.open")} ${t(
            `ElectionMode.${activeRightTab === "Reports" ? "reports" : "electoral"}`
          )}`}
          overlayStyle={{ zIndex: 10000 }}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <Button
            style={{ pointerEvents: "auto" }}
            type="default"
            shape="circle"
            size="large"
            className="bg-[#0048] text-white border-none shadow-lg transition-all duration-300 ease-in-out hover:scale-110 flex items-center justify-center"
            onClick={() => {
              setShowNotesPanel(false);
              setShowMlaPanel(false);
              setShowNewsPanel(false);
              setShowRightPanel(!showRightPanel);
            }}
            icon={
              showRightPanel ? (
                <ChevronRight size={22} />
              ) : (
                <ChevronLeft size={22} />
              )
            }
          />
        </Tooltip>

        <Tooltip
          placement="left"
          title={t(showNotesPanel ? "ElectionMode.closeNotes" : "ElectionMode.openNotes")}
          overlayStyle={{ zIndex: 10000 }}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <Button
            style={{ pointerEvents: "auto" }}
            type="default"
            shape="circle"
            size="large"
            className="bg-[#0048] text-white border-none shadow-lg transition-all duration-300 ease-in-out hover:scale-110 flex items-center justify-center"
            icon={<StickyNote size={20} />}
            onClick={() => {
              setShowNotesPanel(!showNotesPanel);
              setShowMlaPanel(false);
              setShowNewsPanel(false);
              setShowRightPanel(false);
            }}
          />
        </Tooltip>

        <Tooltip
          placement="left"
          title={t(showMlaPanel ? "ElectionMode.closeMlaAffidavit" : "ElectionMode.openMlaAffidavit")}
          overlayStyle={{ zIndex: 10000 }}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <Button
            style={{ pointerEvents: "auto" }}
            type="default"
            shape="circle"
            size="large"
            className="bg-[#0048] text-white border-none shadow-lg transition-all duration-300 ease-in-out hover:scale-110 flex items-center justify-center"
            icon={<FileSpreadsheet size={20} />}
            onClick={() => {
              setShowMlaPanel(!showMlaPanel);
              setShowNotesPanel(false);
              setShowNewsPanel(false);
              setShowRightPanel(false);
            }}
          />
        </Tooltip>

        <Tooltip
          placement="left"
          title={t(showNewsPanel ? "ElectionMode.closeNews" : "ElectionMode.openNews")}
          overlayStyle={{ zIndex: 10000 }}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <Button
            style={{ pointerEvents: "auto" }}
            type="default"
            shape="circle"
            size="large"
            className="bg-[#0048] text-white border-none shadow-lg transition-all duration-300 ease-in-out hover:scale-110 flex items-center justify-center"
            icon={<Newspaper size={20} />}
            onClick={() => {
              setShowNewsPanel(!showNewsPanel);
              setShowMlaPanel(false);
              setShowNotesPanel(false);
              setShowRightPanel(false);
            }}
          />
        </Tooltip>
      </div>

      {/* Left panel toggle button */}
      <div className="absolute top-[46%] left-4 flex flex-col gap-3 z-[9999]">
        <Tooltip
          placement="right"
          title={t(leftPanelOpen ? "ElectionMode.closePanel" : "ElectionMode.openPanel")}
          overlayStyle={{ zIndex: 10000 }}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <Button
            style={{ pointerEvents: "auto" }}
            type="default"
            shape="circle"
            size="large"
            className="bg-[#0048] text-white border-none shadow-lg transition-all duration-300 ease-in-out hover:scale-110 flex items-center justify-center"
            onClick={() => setLeftPanelOpen((prev) => !prev)}
            icon={
              leftPanelOpen ? (
                <ChevronLeft size={22} />
              ) : (
                <ChevronRight size={22} />
              )
            }
          />
        </Tooltip>
      </div>

      {/* Left Panel */}
      <AnimatePresence>
        {leftPanelOpen && (
          <motion.div
            key="left-panel"
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="absolute z-[999]"
          >
            <LeftPanel
              data={data}
              filters={filters}
              isLoading={isLoading}
              onDistrictChange={handleDistrictChange}
              onConstituencyChange={handleConstituencyChange}
              onPartyChange={(party) =>
                setFilters((prev) => ({ ...prev, party }))
              }
              parties={parties}
              overview={overview}
              community={community}
              religion={religion}
              age={age}
              activeCaste={activeCaste}
              setActiveCaste={setActiveCaste}
              activeReligion={activeReligion}
              setActiveReligion={setActiveReligion}
              activeGender={activeGender}
              setActiveGender={setActiveGender}
              activeAge={activeAge}
              setActiveAge={setActiveAge}
              casteNumber={casteNumber}
              religionNumber={religionNumber}
              genderNumber={genderNumber}
              genderLabel={genderLabel}
              t={t}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Right Panel — Electoral / Reports tabs */}
      <AnimatePresence>
        {showRightPanel && (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <RightPanel
              electionModeId={electionModeId}
              filters={filters}
              districts={data?.filters?.districts || []}
              constituencyOptions={data?.filters?.constituencies || []}
              parties={parties}
              panels={panels}
              communityChart={communityChart}
              religionChart={religionChart}
              expandedPanels={expandedPanels}
              onToggle={togglePanel}
              activeTab={activeRightTab}
              onActiveTabChange={setActiveRightTab}
              quickReportRequest={quickReportRequest}
              t={t}
              districtLabel={districtLabel}
              constituencyLabel={constituencyLabel}
              hideDistrict={isUrbanAssembly}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notes Panel */}
      <AnimatePresence>
        {showNotesPanel && (
          <motion.div
            key="notes"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <NotesPanel
              electionModeId={electionModeId}
              filters={filters}
              districts={data?.filters?.districts || []}
              constituencies={data?.filters?.constituencies || []}
              parties={parties}
              quickNoteRequest={quickNoteRequest}
              t={t}
              districtLabel={districtLabel}
              constituencyLabel={constituencyLabel}
              hideDistrict={isUrbanAssembly}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* MLA Affidavit Panel */}
      <AnimatePresence>
        {showMlaPanel && (
          <motion.div
            key="mla-affidavit"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <div className="election-dashboard-right card-wrapper">
              <MlaAffidavitPanel
                electionModeId={electionModeId}
                filters={filters}
                t={t}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* News Panel */}
      <AnimatePresence>
        {showNewsPanel && (
          <motion.div
            key="news-panel"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <div
              className="election-dashboard-right card-wrapper"
              style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
            >
              <div className="flex items-center gap-2 mb-3 flex-shrink-0">
                <Newspaper size={16} className="text-orange-500" />
                <span className="font-semibold text-sm text-black">{t("ElectionMode.mediaIntelligence")}</span>
                <button
                  type="button"
                  onClick={openMediaIntelligencePage}
                  className="ml-auto text-[11px] font-semibold text-[#0b65c2] hover:text-[#0952a8] flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                  title={t("ElectionMode.openFullPage")}
                >
                  {t("ElectionMode.viewAll")}
                  <ChevronRight size={14} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <NewsPanelClient
                  electionModeId={electionModeId}
                  filters={filters}
                  t={t}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom stats bar */}
      <div className="election-dashboard-bottom card-wrapper">
        <BottomStat
          label={t("ElectionMode.totalSeats")}
          value={bottomStats.totalSeats}
          sub={
            electionModeType === "assembly"
              ? t("ElectionMode.villages")
              : t("ElectionMode.stateAssembly")
          }
          // sub={
          //   filters.constituency
          //     ? filters.constituency
          //     : filters.district
          //     ? `${filters.district} ${districtLabel}`
          //     : filters.region
          //     ? t("ElectionMode.regionScope", { region: filters.region })
          //     : t("ElectionMode.stateAssembly")
          // }
        />

        <BottomStat
          label={t("ElectionMode.avgVoteShare")}
          value={`${formatPercent(bottomStats.avgVoteShare) || 0}`}
          sub={filters.party}
        />
        {(
          bottomStats.isConstituencySelected
            ? bottomStats.topPartiesInConstituency
            : bottomStats.partyWins
        )?.slice(0, 4).map((party, index) => (
          <PartyStat
            key={party.party}
            party={party.party}
            value={
              bottomStats.isConstituencySelected
                ? formatPercent(party.voteShare)
                : party.wins
            }
            sub={
              bottomStats.isConstituencySelected
                ? ""
                : electionModeType === "assembly"
                ? isRuralAssembly
                  ? t("ElectionMode.village")
                  : t("ElectionMode.ward")
                : t("ElectionMode.seats")
            }
            rankLabel={t(
              ["ElectionMode.firstParty", "ElectionMode.secondParty", "ElectionMode.thirdParty", "ElectionMode.fourthParty"][index] ||
                "ElectionMode.fourthParty"
            )}
          />
        ))}
      </div>
    </div>
  );
};

export default ElectionModeDashboard;